import { useState } from "react";
import { ROLES, salaryOn } from "@dentacore/shared-types";
import type { AccountingLocation, SalaryPerson, SaveSalary } from "@dentacore/shared-types";

import { useAuth } from "../auth/auth-context";
import { roleLabel } from "../settings/people-section";
import { money, monthName, niceDay, parseDay, percent } from "./format";
import { LastDayDialog, SalaryDialog } from "./salary-dialogs";
import styles from "./accounting.module.css";
import { PagedRows } from "../ui/paging";

/** Left before today — listed last, and their salary no longer counts. */
function hasLeft(person: SalaryPerson, today: string): boolean {
  return person.endedOn !== null && person.endedOn < today;
}

/** Employed today: started (or no start recorded) and not yet past their last day. */
function employedOn(person: SalaryPerson, day: string): boolean {
  return (
    (person.startedOn === null || person.startedOn <= day) &&
    (person.endedOn === null || person.endedOn >= day)
  );
}

/** "12.5 h" — rota minutes as hours, one decimal at most. */
function hours(minutes: number): string {
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(minutes / 60)} h`;
}

/**
 * Salaries — everyone's monthly salary, its history and where it is counted
 * (AC-D3, AC-D10–AC-D13, AC-D18).
 *
 * **Only Admin and Finance officer ever reach this** (AC-D13): the route and
 * the API both ask for `accounting.salaries.view` (QA4-D8), and the Staff page, which everyone
 * can open, never shows money.
 *
 * A person opens beside the list, as an item does in Inventory. Their panel
 * shows what this month has cost so far and how it splits across locations —
 * by rota hours (AC-D7), or evenly when they have none (AC-D12) — and every
 * salary they have had, because a change takes effect from a date and the
 * old figure is kept (AC-D10).
 */
export function SalariesTab({
  people,
  today,
  locations,
  currency,
  canManage,
  busy,
  onSaveSalary,
  onSetLastDay,
}: {
  people: readonly SalaryPerson[] | null;
  today: string;
  locations: readonly AccountingLocation[];
  currency: string;
  canManage: boolean;
  busy: boolean;
  onSaveSalary: (person: SalaryPerson, body: SaveSalary) => Promise<boolean>;
  onSetLastDay: (person: SalaryPerson, endedOn: string | null) => Promise<boolean>;
}): React.JSX.Element {
  const [personId, setPersonId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"salary" | "lastDay" | null>(null);
  // Nobody sets their own pay or last day but an administrator (QF-D6).
  const { user } = useAuth();
  const mayChange = (who: SalaryPerson): boolean =>
    canManage && (who.userId !== user?.id || user.canAdminister);

  if (people === null) {
    return (
      <div className={styles.card}>
        <p className={styles.blank}>Loading…</p>
      </div>
    );
  }

  if (people.length === 0) {
    return (
      <div className={styles.card}>
        <p className={styles.blank}>Nobody is on the team yet.</p>
      </div>
    );
  }

  // Still here first, then by job as the product orders roles, then by name.
  const sorted = [...people].sort(
    (a, b) =>
      Number(hasLeft(a, today)) - Number(hasLeft(b, today)) ||
      ROLES.indexOf(a.job) - ROLES.indexOf(b.job) ||
      a.name.localeCompare(b.name),
  );
  const person = sorted.find((row) => row.userId === personId) ?? sorted[0] ?? null;
  const thisYear = `${String(parseDay(today).year)}-`;

  return (
    <div className={styles.body}>
      <section className={styles.card} data-testid="salary-list">
        <div className={styles.columns} aria-hidden="true">
          <span>Person</span>
          <span>Monthly salary</span>
        </div>
        {/* Paged to the room it has, like every list here (fit every screen size). */}
        <PagedRows rows={sorted} noun="people">
          {(page) =>
            page.map((row) => {
              const left = hasLeft(row, today);
              // Someone who has left shows the salary they left on.
              const asOf = left && row.endedOn !== null ? row.endedOn : today;
              const salary = salaryOn(row.salaries, asOf);
              const on = person !== null && row.userId === person.userId;
              const sub =
                left && row.endedOn !== null
                  ? ` · left ${niceDay(row.endedOn)}${row.endedOnAssumed ? " (assumed)" : ""}`
                  : row.endedOn !== null
                    ? ` · last day ${niceDay(row.endedOn)}`
                    : row.startedOn !== null && row.startedOn.startsWith(thisYear)
                      ? ` · joined ${niceDay(row.startedOn)}`
                      : "";
              return (
                <button
                  key={row.userId}
                  type="button"
                  className={on ? `${styles.row} ${styles.rowOn}` : styles.row}
                  aria-current={on ? "true" : undefined}
                  onClick={() => setPersonId(row.userId)}
                  data-card=""
                  data-testid="salary-row"
                >
                  <span className={styles.rowName}>
                    {row.name}
                    <span className={styles.rowSub}>
                      {roleLabel(row.job)}
                      {sub}
                    </span>
                  </span>
                  <span
                    className={left ? `${styles.rowMoney} ${styles.rowMoneyGone}` : styles.rowMoney}
                  >
                    {salary === null ? "—" : money(salary.monthlyCents, currency)}
                  </span>
                </button>
              );
            })
          }
        </PagedRows>
      </section>

      <section className={styles.card}>
        {person === null ? (
          <p className={styles.blank}>Pick a person.</p>
        ) : (
          <PersonPanel
            key={person.userId}
            person={person}
            today={today}
            locations={locations}
            currency={currency}
            canManage={mayChange(person)}
            busy={busy}
            onChangeSalary={() => setDialog("salary")}
            onLastDay={() => setDialog("lastDay")}
          />
        )}
      </section>

      {person !== null && dialog === "salary" && (
        <SalaryDialog
          person={person}
          today={today}
          currency={currency}
          busy={busy}
          onSave={(body) => onSaveSalary(person, body)}
          onClose={() => setDialog(null)}
        />
      )}
      {person !== null && dialog === "lastDay" && (
        <LastDayDialog
          person={person}
          today={today}
          busy={busy}
          onSave={(endedOn) => onSetLastDay(person, endedOn)}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

function PersonPanel({
  person,
  today,
  locations,
  currency,
  canManage,
  busy,
  onChangeSalary,
  onLastDay,
}: {
  person: SalaryPerson;
  today: string;
  locations: readonly AccountingLocation[];
  currency: string;
  canManage: boolean;
  busy: boolean;
  onChangeSalary: () => void;
  onLastDay: () => void;
}): React.JSX.Element {
  const current = salaryOn(person.salaries, today);
  const employed = employedOn(person, today);
  const { thisMonth } = person;
  const totalMinutes = thisMonth.split.reduce((sum, part) => sum + part.minutes, 0);
  const history = [...person.salaries].sort((a, b) =>
    b.effectiveFrom.localeCompare(a.effectiveFrom),
  );
  const locationName = (id: string): string =>
    locations.find((location) => location.id === id)?.name ?? "Another location";

  return (
    <div className={styles.panel} data-testid="salary-panel">
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>{person.name}</h2>
        <span className={`${styles.chip} ${styles.chipPlain}`}>{roleLabel(person.job)}</span>
        <span className={styles.spacer} />
        {canManage && (
          <>
            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={onChangeSalary}
              data-testid="salary-change"
            >
              Change salary
            </button>
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={onLastDay}
              data-testid="salary-last-day"
            >
              {person.endedOn === null ? "Set last day" : "Change last day"}
            </button>
          </>
        )}
      </div>

      <div className={styles.mini}>
        <div className={styles.miniFact}>
          <div className={styles.factKey}>Monthly salary now</div>
          <div className={styles.miniValue}>
            {current !== null && employed ? money(current.monthlyCents, currency) : "—"}
          </div>
        </div>
        <div className={styles.miniFact}>
          <div className={styles.factKey}>Started</div>
          <div className={styles.miniValue}>
            {person.startedOn === null ? "—" : niceDay(person.startedOn)}
          </div>
        </div>
        <div className={styles.miniFact}>
          <div className={styles.factKey}>Last day</div>
          <div className={styles.miniValue} data-testid="salary-last-day-value">
            {person.endedOn === null ? "—" : niceDay(person.endedOn)}
          </div>
          {/* Deactivated before a last day was asked for (QF-D7): their salary
              stops at their account's last change. Saying so lets an admin set it. */}
          {person.endedOnAssumed && (
            <div className={styles.factKey} data-testid="salary-last-day-assumed">
              Not recorded — taken from when their account last changed. Set the real one.
            </div>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{monthName(parseDay(today).month)} so far</h3>
        {thisMonth.cents !== 0 ? (
          <>
            <p className={styles.note}>
              {thisMonth.days} of {thisMonth.daysInMonth} days counted, so{" "}
              {money(thisMonth.cents, currency)} so far.{" "}
              {thisMonth.byRota
                ? "Split by the hours on their rota."
                : "No rota hours, so split evenly across their locations."}
            </p>
            <div className={styles.tableWrap}>
              <table className={styles.table} data-testid="salary-split">
                <thead>
                  <tr>
                    <th>Location</th>
                    {thisMonth.byRota && <th className={styles.right}>Hours</th>}
                    <th className={styles.right}>Share</th>
                    <th className={styles.right}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {thisMonth.split.map((part) => (
                    <tr key={part.branchId}>
                      <td>{locationName(part.branchId)}</td>
                      {thisMonth.byRota && <td className={styles.right}>{hours(part.minutes)}</td>}
                      <td className={styles.right}>
                        {percent(
                          thisMonth.byRota
                            ? totalMinutes > 0
                              ? part.minutes / totalMinutes
                              : 0
                            : 1 / thisMonth.split.length,
                        )}
                      </td>
                      <td className={styles.right}>{money(part.cents, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className={styles.note}>
            Nothing counted this month
            {person.endedOn !== null && person.endedOn < today
              ? `: their last day was ${niceDay(person.endedOn)}`
              : ""}
            .
          </p>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Salary history</h3>
        {history.length === 0 ? (
          <p className={styles.note}>No salary recorded yet.</p>
        ) : (
          <PagedRows rows={history} per={12} noun="changes">
            {(shown) => (
              <div className={styles.tableWrap}>
                <table className={styles.table} data-testid="salary-history">
                  <thead>
                    <tr>
                      <th>From</th>
                      <th className={styles.right}>Monthly</th>
                      <th>
                        <span className={styles.srOnly}>State</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((entry) => (
                      <tr key={entry.id}>
                        <td>{niceDay(entry.effectiveFrom)}</td>
                        <td className={styles.right}>{money(entry.monthlyCents, currency)}</td>
                        <td className={styles.right}>
                          {entry === current && employed ? (
                            <span className={`${styles.chip} ${styles.chipGood}`}>Current</span>
                          ) : entry.effectiveFrom > today ? (
                            <span className={`${styles.chip} ${styles.chipBlue}`}>Upcoming</span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PagedRows>
        )}
        <p className={styles.note}>
          A change takes effect from its date. Months before it keep the old salary.
        </p>
      </div>
    </div>
  );
}
